import React, { useState, useEffect, useCallback } from 'react';
import { UnpaidInvoice, AgentInvoiceAnalysis } from '../../../types/organizations';
import { authFetch } from '../../../utils/authFetch';
import { useUser } from '../../../context/UserContext';
import InvoiceCard from '../InvoiceCard';

interface InvoicesTabProps {
  invoices: UnpaidInvoice[];
  loading: boolean;
  loadingMore?: boolean;
  error: string;
  organizationId: string;
  onViewAnalyses: (type: string, analyses: AgentInvoiceAnalysis[]) => void;
  onRefreshInvoices?: () => void;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
  totalCount?: number;
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({ invoices, loading, loadingMore, error, organizationId, onViewAnalyses, onRefreshInvoices, onLoadMore, canLoadMore, totalCount }) => {
  const { accessToken, setUserAndToken } = useUser();
  const [preparingReminders, setPreparingReminders] = useState(false);
  const [prepareError, setPrepareError] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';
      const response = await authFetch(
        `${API_BASE}/redis-jobs/${jobId}/status`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const result = await response.json();
        setJobStatus(result.status);
        
        // Update localStorage with current status
        localStorage.setItem(`collection_reminder_status_${organizationId}`, result.status);
        
        // If job is completed or failed, stop polling and clean up
        if (['finished', 'failed'].includes(result.status)) {
          if (result.status === 'finished') {
            // Refresh invoice data to show new collection reminders and insights
            if (onRefreshInvoices) {
              onRefreshInvoices();
            }
          } else if (result.status === 'failed') {
            setPrepareError('Collection reminder job failed. Please try again.');
          }
          // Clear job tracking and localStorage after showing result
          setTimeout(() => {
            setJobId(null);
            setJobStatus(null);
            localStorage.removeItem(`collection_reminder_job_${organizationId}`);
            localStorage.removeItem(`collection_reminder_status_${organizationId}`);
          }, 5000);
        } else {
          // Continue polling if job is still running
          setTimeout(() => pollJobStatus(jobId), 5000);
        }
      }
    } catch (err) {
      console.error('Error polling job status:', err);
      // Stop polling on error and clean up
      setJobId(null);
      setJobStatus(null);
      localStorage.removeItem(`collection_reminder_job_${organizationId}`);
      localStorage.removeItem(`collection_reminder_status_${organizationId}`);
    }
  }, [organizationId, accessToken, setUserAndToken, onRefreshInvoices]);

  // Check for existing collection reminder jobs on component mount
  useEffect(() => {
    const existingJobId = localStorage.getItem(`collection_reminder_job_${organizationId}`);
    const existingJobStatus = localStorage.getItem(`collection_reminder_status_${organizationId}`);
    
    if (existingJobId && existingJobStatus) {
      setJobId(existingJobId);
      setJobStatus(existingJobStatus);
      
      // If job is still running, resume polling
      if (!['finished', 'failed'].includes(existingJobStatus)) {
        pollJobStatus(existingJobId);
      }
    }
  }, [organizationId, pollJobStatus]);


  const handlePrepareCollectionReminders = async () => {
    setPreparingReminders(true);
    setPrepareError('');

    try {
      const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';
      const response = await authFetch(
        `${API_BASE}/organizations/${organizationId}/collection-reminders`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        accessToken,
        setUserAndToken
      );

      if (!response.ok) {
        throw new Error('Failed to prepare collection reminders');
      }

      const result = await response.json();
      
      if (result.data && result.data.job_id) {
        setJobId(result.data.job_id);
        setJobStatus(result.data.status);
        
        // Save job info to localStorage for persistence
        localStorage.setItem(`collection_reminder_job_${organizationId}`, result.data.job_id);
        localStorage.setItem(`collection_reminder_status_${organizationId}`, result.data.status);
        
        // Start polling for job status
        pollJobStatus(result.data.job_id);
      }

    } catch (err: any) {
      setPrepareError(err.message || 'Failed to prepare collection reminders');
    } finally {
      setPreparingReminders(false);
    }
  };



  return (
    <div className="px-6 pb-6">
      <div className="space-y-4 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900">Unpaid Invoices</h3>
            {typeof totalCount === 'number' && (
              <span className="text-sm text-gray-500">({invoices.length} of {totalCount})</span>
            )}
          </div>
          <button
            onClick={handlePrepareCollectionReminders}
            disabled={Boolean(preparingReminders || invoices.length === 0 || (jobId && jobStatus && !['finished', 'failed'].includes(jobStatus)))}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              Boolean(preparingReminders || invoices.length === 0 || (jobId && jobStatus && !['finished', 'failed'].includes(jobStatus)))
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {preparingReminders ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Queuing...
              </div>
            ) : jobId && jobStatus ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                {jobStatus === 'queued' ? 'Queued' : 
                 jobStatus === 'started' ? 'Processing...' : 
                 jobStatus === 'finished' ? 'Completed' : 
                 jobStatus === 'failed' ? 'Failed' : jobStatus}
              </div>
            ) : (
              'Prepare Collection Reminders'
            )}
          </button>
        </div>

        {error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : null}

        {prepareError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {prepareError}
          </div>
        )}



        {!error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading && invoices.length === 0 ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-gray-600">Loading unpaid invoices...</span>
              </div>
            ) : invoices.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-500 text-lg mb-2">No unpaid invoices found</div>
                <div className="text-gray-400 text-sm">Unpaid invoices will appear here once Xero data is synced.</div>
              </div>
            ) : (
              invoices.map((invoice) => (
                <InvoiceCard
                  key={invoice.invoice_id}
                  invoice={invoice}
                  onViewAnalyses={onViewAnalyses}
                  organizationId={organizationId}
                />
              ))
            )}
          </div>
        )}

        {/* Load More Button */}
        {!error && onLoadMore && canLoadMore && (
          <div className="mt-6 flex flex-col items-center">
            <button
              onClick={onLoadMore}
              className={`px-6 py-2 rounded-lg transition-colors duration-200 flex items-center ${loadingMore ? 'bg-blue-400 text-white cursor-wait' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              disabled={Boolean(loadingMore)}
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Load More Invoices
                </>
              )}
            </button>

            {/* Hint: Showing X of Y */}
            {typeof totalCount === 'number' && (
              <div className="mt-2 text-sm text-gray-500">
                (Showing {invoices.length} of {totalCount} unpaid invoices)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesTab;
